import React from "react";
import { User, ShieldCheck, X } from "lucide-react";
import { Button } from "../../../../components";

const StudentAuthModal = ({
  authenticatingStudent,
  onClose,
  studentSuccess,
  studentError,
  studentAuthMethod,
  setStudentAuthMethod,
  studentOtpSent,
  setStudentOtpSent,
  studentEmail,
  setStudentEmail,
  studentPhone,
  setStudentPhone,
  studentOtp,
  setStudentOtp,
  studentLoading,
  studentTimer,
  onSendStudentOtp,
  onVerifyStudentOtp,
  onGoogleLinkStudent,
}) => {
  if (!authenticatingStudent) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
      <div className="relative w-full max-w-sm bg-white rounded-2xl p-5 shadow-2xl border border-gray-100 flex flex-col gap-4 animate-scale-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-650 transition-colors font-bold text-lg"
        >
          ✕
        </button>

        {studentSuccess ? (
          <div className="flex flex-col items-center justify-center text-center py-6 gap-4 animate-fade-in">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center animate-bounce">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900">
              Linked Successfully!
            </h3>
            <p className="text-sm text-gray-600 font-medium leading-relaxed max-w-xs">
              {authenticatingStudent.name}'s account has been verified and set up for independent login.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center text-center gap-2">
              <div className="bg-red-50 p-3 rounded-full text-red-600 ring-4 ring-red-50/50">
                <User className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-black text-gray-900 tracking-tight">
                Authenticate Student
              </h3>
              <p className="text-xs text-gray-500 font-semibold leading-relaxed">
                Verify student credentials to enable student login.
              </p>
            </div>

            {studentError && (
              <div className="bg-red-50 text-red-700 p-2.5 rounded-lg border border-red-100 text-xs font-semibold">
                <span>{studentError}</span>
              </div>
            )}

            {/* Tab Selector (only if OTP has not been sent yet) */}
            {!studentOtpSent && (
              <div className="flex bg-gray-100 p-1.5 rounded-xl gap-2 w-full text-xs font-bold text-gray-500 mb-2">
                <button
                  type="button"
                  onClick={() => setStudentAuthMethod("otp")}
                  className={`flex-1 py-2 text-center rounded-lg transition ${
                    studentAuthMethod === "otp"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "hover:text-gray-900"
                  }`}
                  disabled={studentLoading}
                >
                  WhatsApp OTP
                </button>
                <button
                  type="button"
                  onClick={() => setStudentAuthMethod("google")}
                  className={`flex-1 py-2 text-center rounded-lg transition ${
                    studentAuthMethod === "google"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "hover:text-gray-900"
                  }`}
                  disabled={studentLoading}
                >
                  Google Link
                </button>
              </div>
            )}

            {studentAuthMethod === "otp" ? (
              <>
                {!studentOtpSent ? (
                  <form onSubmit={onSendStudentOtp} className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-700 tracking-wide">
                        Student Email ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="eg: sam@mail.com"
                        value={studentEmail}
                        onChange={(e) => setStudentEmail(e.target.value)}
                        className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-700 tracking-wide">
                        Student WhatsApp Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2 items-center">
                        <span className="bg-gray-100 border border-gray-200 text-gray-700 font-bold px-3 py-2.5 rounded-lg text-sm select-none">
                          +91
                        </span>
                        <input
                          type="tel"
                          required
                          maxLength={10}
                          placeholder="10-digit number"
                          value={studentPhone}
                          onChange={(e) => setStudentPhone(e.target.value.replace(/\D/g, ""))}
                          className="w-full flex-grow text-sm border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                        />
                      </div>
                    </div>

                    <div className="mt-2">
                      <Button
                        version="secondary"
                        text={studentLoading ? "Sending..." : "Send OTP"}
                        type="submit"
                        loading={studentLoading}
                        isDisabled={studentPhone.length !== 10 || !studentEmail || studentLoading}
                        width="100%"
                      />
                    </div>
                  </form>
                ) : (
                  <form onSubmit={onVerifyStudentOtp} className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <p className="text-xs text-gray-650 font-semibold leading-relaxed">
                        Enter 6-digit OTP code sent to <strong>+91 *******{studentPhone.slice(-4)}</strong>:
                      </p>
                      <input
                        type="text"
                        required
                        maxLength={6}
                        placeholder="Enter OTP"
                        value={studentOtp}
                        onChange={(e) => setStudentOtp(e.target.value.replace(/\D/g, ""))}
                        className="w-full text-center text-lg font-bold tracking-widest border border-slate-300 rounded-xl px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
                      />
                    </div>

                    <div className="flex flex-col gap-2.5 mt-1">
                      <Button
                        version="secondary"
                        text={studentLoading ? "Verifying..." : "Verify & Complete Link"}
                        type="submit"
                        loading={studentLoading}
                        isDisabled={studentOtp.length !== 6 || studentLoading}
                        width="100%"
                      />

                      <div className="flex justify-between items-center text-[11px] mt-1">
                        <button
                          type="button"
                          onClick={() => setStudentOtpSent(false)}
                          className="text-gray-500 hover:text-gray-750 font-bold transition-colors"
                        >
                          Change Details
                        </button>

                        <div>
                          {studentTimer > 0 ? (
                            <span className="text-gray-400 font-semibold">
                              Resend in {studentTimer}s
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={onSendStudentOtp}
                              className="text-blue-600 hover:text-blue-800 font-bold transition-colors"
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-4 text-center py-2 animate-fade-in">
                <p className="text-xs text-slate-500 font-semibold leading-relaxed max-w-xs mx-auto">
                  This will open a Google Sign-In popup. Please select your child's Google account to register their email credentials.
                </p>

                <button
                  type="button"
                  onClick={onGoogleLinkStudent}
                  disabled={studentLoading}
                  className="w-full py-2.5 px-4 border border-slate-300 hover:bg-slate-50 text-slate-700 font-black rounded-xl text-xs flex items-center justify-center gap-2 transition duration-200"
                >
                  {studentLoading ? (
                    <span>Linking...</span>
                  ) : (
                    <>
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                        />
                      </svg>
                      <span>Link Google Account</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentAuthModal;
